import { useState, useRef, useEffect, useMemo } from 'react';
import { useAiIntelligenceStore } from '../store/ai-intelligence-store';
import { formatCurrency } from '@/lib/utils/format';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { ChevronDown, Search, X } from 'lucide-react';

interface QuoteSelectorProps {
  value: string;
  onChange: (quoteId: string) => void;
  label?: string;
}

export function QuoteSelector({ value, onChange, label = 'Cotización' }: QuoteSelectorProps) {
  const quotes = useAiIntelligenceStore((s) => s.quotes);
  const isLoading = useAiIntelligenceStore((s) => s.isLoadingSelectors);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = quotes.find((q) => q.id === value);

  const filtered = useMemo(() => {
    if (!search) return quotes;
    const term = search.toLowerCase();
    return quotes.filter(
      (q) =>
        q.code.toLowerCase().includes(term) ||
        q.clientName.toLowerCase().includes(term) ||
        q.status.toLowerCase().includes(term)
    );
  }, [quotes, search]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setSearch('');
    }
  }, [open]);

  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={isLoading}
            className="w-full flex items-center justify-between rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className={selected ? 'text-foreground truncate' : 'text-muted-foreground'}>
              {isLoading
                ? 'Cargando...'
                : selected
                  ? `${selected.code} — ${selected.clientName}`
                  : 'Seleccionar cotización...'}
            </span>
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {value && (
                <span
                  role="button"
                  className="p-0.5 rounded hover:bg-muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange('');
                  }}
                >
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                </span>
              )}
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-0"
          sideOffset={4}
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Buscar por código, cliente o estado..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-sm text-muted-foreground text-center">Sin resultados</p>
            ) : (
              filtered.map((q) => (
                <button
                  key={q.id}
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors ${
                    q.id === value ? 'bg-accent/50 font-medium' : ''
                  }`}
                  onClick={() => {
                    onChange(q.id);
                    setOpen(false);
                  }}
                >
                  <span className="font-medium">{q.code}</span>
                  <span className="text-muted-foreground"> — {q.clientName}</span>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {formatCurrency(q.finalPrice, q.currency)} · {q.status} · {q.modelName}
                  </div>
                </button>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
